import { useState } from 'react';
import { Box, Chip, Stack, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { IntegrationsList } from '@/features/integrations';
import { usePageTitle } from '@/features/pageTitle';
import { PublicApiSettings } from '@/features/publicApi';
import { PageContainer } from '@/shared/ui/PageContainer';
import { CustomTypography } from '@/shared/ui/Typography';

type IntegrationTab = 'amocrm' | 'bitrix24' | 'sbercrm' | 'openApi';

const INTEGRATION_TABS: IntegrationTab[] = [
  'amocrm',
  'bitrix24',
  'sbercrm',
  'openApi',
];

export const IntegrationsPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<IntegrationTab>('amocrm');
  usePageTitle(t('common:menu.integration'));

  return (
    <PageContainer>
      <Stack spacing={2}>
        <Tabs
          value={activeTab}
          onChange={(_, value: IntegrationTab) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 46,
            '& .MuiTabs-flexContainer': {
              gap: 1,
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          {INTEGRATION_TABS.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={
                <Stack alignItems="flex-start" spacing={0.25}>
                  <CustomTypography
                    sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}
                  >
                    {t(`integrations:tabs.${tab}.title`)}
                  </CustomTypography>
                  <CustomTypography
                    sx={{
                      color: 'inherit',
                      fontSize: 12,
                      lineHeight: 1.2,
                      opacity: 0.72,
                    }}
                  >
                    {t(`integrations:tabs.${tab}.caption`)}
                  </CustomTypography>
                </Stack>
              }
              sx={{
                alignItems: 'flex-start',
                border: '1px solid',
                borderColor: 'custom.grey5',
                borderRadius: '12px',
                color: 'custom.black.60',
                minHeight: 46,
                minWidth: { xs: 180, md: 205 },
                p: '9px 12px',
                textAlign: 'left',
                textTransform: 'none',
                transition: 'all 160ms ease',
                '&.Mui-selected': {
                  backgroundColor: 'common.white',
                  borderColor: 'primary.main',
                  boxShadow: '0 10px 28px rgba(0, 120, 212, 0.12)',
                  color: 'primary.main',
                },
                '&:hover': {
                  backgroundColor: 'common.white',
                  borderColor: 'primary.main',
                },
              }}
            />
          ))}
        </Tabs>

        <Box hidden={activeTab !== 'amocrm'}>
          {activeTab === 'amocrm' ? <IntegrationsList /> : null}
        </Box>
        <Box hidden={activeTab !== 'bitrix24'}>
          {activeTab === 'bitrix24' ? (
            <ComingSoonIntegration tab="bitrix24" />
          ) : null}
        </Box>
        <Box hidden={activeTab !== 'sbercrm'}>
          {activeTab === 'sbercrm' ? (
            <ComingSoonIntegration tab="sbercrm" />
          ) : null}
        </Box>
        <Box hidden={activeTab !== 'openApi'}>
          {activeTab === 'openApi' ? <PublicApiSettings /> : null}
        </Box>
      </Stack>
    </PageContainer>
  );
};

const ComingSoonIntegration = ({ tab }: { tab: 'bitrix24' | 'sbercrm' }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        backgroundColor: 'common.white',
        border: '1px solid',
        borderColor: 'custom.grey6',
        borderRadius: '16px',
        p: { xs: 2, md: 2.5 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <Stack spacing={0.75} sx={{ flex: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <CustomTypography variant="h4" sx={{ fontWeight: 600 }}>
              {t(`integrations:tabs.${tab}.title`)}
            </CustomTypography>
            <Chip
              size="small"
              label={t('integrations:comingSoon.status')}
              sx={{ bgcolor: 'custom.grey6' }}
            />
          </Stack>
          <CustomTypography sx={{ color: 'custom.black.40', maxWidth: 720 }}>
            {t('integrations:comingSoon.description')}
          </CustomTypography>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ flex: 1.1 }}
        >
          {['connection', 'sync', 'settings'].map((item) => (
            <Box
              key={item}
              sx={{
                border: '1px solid',
                borderColor: 'custom.grey6',
                borderRadius: '12px',
                px: 1.5,
                py: 1,
                flex: 1,
              }}
            >
              <CustomTypography sx={{ fontWeight: 600, fontSize: 14 }}>
                {t(`integrations:comingSoon.items.${item}.title`)}
              </CustomTypography>
              <CustomTypography sx={{ color: 'custom.black.40', fontSize: 13 }}>
                {t(`integrations:comingSoon.items.${item}.caption`)}
              </CustomTypography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
